import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'

import { desktopCSS, mobileAndTabletCSS, mobileCSS } from '@measures/responsive'
import { WidgetKind } from '@utils/registry'
import InvertedColorsContext from '@contexts/invertedColors'
import useBodyScroll from '@hooks/useBodyScroll'
import {
  useIsVideoCombinationWrapperCreated,
  useShowVideoCombination,
} from '@hooks/useShowVideoCombination'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useViewportType from '@hooks/useViewport/useViewportType'
import useViewportTracking from '@hooks/useViewportTracking'
import { JSONWidgetType as AdRingierJSONWidgetType } from '@widgets/AdRingier'
import { TeaserBiteAPIProps } from '@widgets/TeaserBite'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import JSONRenderer from '@components/JSONRenderer'
import SectionHeader from '@components/SectionHeader'
import Swiper, { ArrowsVisibilityType, SwiperClass } from '@components/Swiper'
import NavigationArrows from '@components/Swiper/NavigationArrows'
import FullScreenVideoPortal from '@components/Video/BlickBites/FullScreenVideoPortal'
import { getBiteIdFromHash } from '@components/Video/BlickBites/utils'

export interface BlickBitesItem extends TeaserBiteAPIProps {
  kind: WidgetKind
}

export type BlickBitesAd = AdRingierJSONWidgetType

export interface BlickBitesData {
  content: BlickBitesItem[]
  previousCursor?: number
}

export interface BlickBitesWidgetAPIProps {
  title: string
  accentColor: string
  items: BlickBitesItem[]
  ad: BlickBitesAd
}

interface FadeGradientProps {
  isHidden: boolean
  itemsCount: number
  direction: 'left' | 'right'
}

const BlickBitesWrapper = styled.div`
  ${({
    theme: {
      colors: { staticBlack },
      spacing: { spacing32, spacing56 },

      measures: { stretchBackground },
    },
  }) => css`
    background-color: ${staticBlack};

    ${stretchBackground()};

    padding-top: ${spacing32};
    padding-bottom: ${spacing32};

    ${desktopCSS(css`
      padding: ${spacing56};
    `)}
  `}
`

const StyledNavigationArrows = styled(NavigationArrows)<{ itemsCount: number }>`
  ${({ itemsCount }) => css`
    ${itemsCount <= 2 &&
    mobileAndTabletCSS(css`
      display: none;
    `)}

    ${itemsCount <= 3 &&
    desktopCSS(css`
      display: none;
    `)}
  `}
`

const SwiperWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16, spacing32 },
      measures: { outerPadding },
    },
  }) => css`
    position: relative;
    margin-top: ${spacing16};

    ${mobileCSS(css`
      margin-right: -${outerPadding.mobile};
    `)}

    ${desktopCSS(css`
      margin-top: ${spacing32};
    `)}
  `}
`

const FadeGradient = styled.div<FadeGradientProps>`
  ${({
    theme: {
      colors: { staticBlack },
      utils: { opacity },
    },
    isHidden,
    itemsCount,
    direction,
  }) => css`
    width: 40px;
    height: 100%;

    position: absolute;
    z-index: 1;
    top: 0;

    pointer-events: none;

    transition: opacity 400ms ease-in-out;

    ${direction}: 0;
    background: linear-gradient(
      to ${direction},
      ${opacity(staticBlack, '00')},
      ${staticBlack}
    );

    ${isHidden &&
    css`
      opacity: 0;
    `}

    ${desktopCSS(css`
      width: 80px;
    `)};

    ${itemsCount <= 2 &&
    css`
      ${mobileAndTabletCSS(css`
        display: none;
      `)}
    `};

    ${itemsCount <= 3 &&
    desktopCSS(css`
      display: none;
    `)}
  `}
`

const StyledSwiper = styled(Swiper)`
  ${({
    theme: {
      spacing: { spacing16, spacing24 },
    },
  }) => css`
    > .swiper-items-container {
      grid-gap: ${spacing16};

      ${desktopCSS(css`
        grid-gap: ${spacing24};
      `)}
    }
  `}
`

const SwiperSlide = styled.div`
  ${({
    theme: {
      measures: { outerPadding },
    },
  }) => css`
    width: 220px;

    ${mobileCSS(css`
      width: 150px;

      &:last-child {
        margin-right: ${outerPadding.mobile};
        scroll-margin-right: ${outerPadding.mobile};
      }
    `)}
  `}
`

const BlickBites: FunctionComponent<BlickBitesWidgetAPIProps> = ({
  title,
  accentColor,
  items,
  ad,
}) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const viewportType = useViewportType()
  const isDesktop = viewportType === 'desktop'

  const swiperRef = useRef<SwiperClass | null>(null)
  const isBodyScrollLocked = useRef<boolean>(false)

  const [showFullscreen, setShowFullscreen] = useState<boolean>(false)
  const [biteId, setBiteId] = useState<string>('')

  const [activeBlickBiteIndex, setActiveBlickBiteIndex] = useState<number>(0)
  const [arrowsVisibility, setArrowsVisibility] =
    useState<ArrowsVisibilityType>('next')

  const shouldHideLeftGradient = useMemo(
    () => ['none', 'next', 'both'].includes(arrowsVisibility || ''),
    [arrowsVisibility],
  )

  const shouldHideRightGradient = useMemo(
    () => ['none', 'prev'].includes(arrowsVisibility || ''),
    [arrowsVisibility],
  )

  const { lockBodyScroll, unlockBodyScroll } = useBodyScroll()

  useEffect(() => {
    if (!queryClient.getQueryData<boolean>(['blick-bites-hash-check'])) {
      queryClient.setQueryData<boolean>(['blick-bites-hash-check'], true)
      const routeAsPath = router.asPath
      const biteId = getBiteIdFromHash(routeAsPath)
      if (biteId) {
        setBiteId(biteId)
        setShowFullscreen(true)
      }
    }
  }, [queryClient, router])

  useEffect(() => {
    return () => {
      if (isBodyScrollLocked.current) {
        unlockBodyScroll()
        isBodyScrollLocked.current = false
      }
    }
  }, [unlockBodyScroll])

  useEffect(() => {
    if (showFullscreen) {
      lockBodyScroll()
      isBodyScrollLocked.current = true
    } else {
      if (isBodyScrollLocked.current) {
        unlockBodyScroll()
        isBodyScrollLocked.current = false
      }
    }
  }, [lockBodyScroll, unlockBodyScroll, showFullscreen])

  const blickBitesItemClickHandler = useCallback<TrackingFnType>(
    () => ({
      event: 'element_click',
      element: 'blick_bites_widget',
    }),
    [],
  )

  const blickBitesWidgetImpressionHandler = useCallback<TrackingFnType>(
    () => ({
      event: 'element_impression',
      element: 'blick_bites_widget',
    }),
    [],
  )

  const trackItemClick = useTracking(blickBitesItemClickHandler)
  const trackWidgetImpression = useTracking(blickBitesWidgetImpressionHandler)

  const viewportRef = useViewportTracking({
    track: true,
    onImpression: trackWidgetImpression,
  })

  const handleOnItemClick = useCallback<
    (_id: string, indexPosition: number) => void
  >(
    (_id, indexPosition) => {
      setActiveBlickBiteIndex(indexPosition || 0)
      setShowFullscreen(true)
      trackItemClick()
    },
    [trackItemClick],
  )

  const handleOnClose = useCallback(() => {
    setBiteId('')
    setShowFullscreen(false)
  }, [])

  const onInit = useCallback(
    (swiper: any) => {
      swiperRef.current = swiper
    },
    [swiperRef],
  )

  const onArrowsVisibility = useCallback((arrow: ArrowsVisibilityType) => {
    setArrowsVisibility(arrow)
  }, [])

  const itemsWithOnClick = useMemo<
    (BlickBitesItem & {
      onClick: () => void
    })[]
  >(
    () =>
      items?.map((swiperData, index) => {
        return {
          onClick: () => handleOnItemClick(swiperData?.articleId, index),
          ...swiperData,
        }
      }) || [],
    [items, handleOnItemClick],
  )

  const widgetContent = useMemo(() => {
    return (
      <InvertedColorsContext.Provider value={true}>
        <BlickBitesWrapper ref={viewportRef}>
          <SectionHeader title={title} accentColor={accentColor}>
            <StyledNavigationArrows
              swiperRef={swiperRef}
              arrowsVisibility={arrowsVisibility}
              itemsCount={items?.length ?? 0}
            />
          </SectionHeader>
          <SwiperWrapper>
            <StyledSwiper
              spaceBetween={16}
              spaceTopBottom={0}
              slidesPerGroup={isDesktop ? 3 : 1}
              initialSlide={0}
              onInit={onInit}
              onArrowsVisibility={onArrowsVisibility}
              slides={itemsWithOnClick?.map(({ onClick, ...item }) => (
                <SwiperSlide key={item.articleId} onClick={onClick}>
                  <JSONRenderer>{item}</JSONRenderer>
                </SwiperSlide>
              ))}
            />
            <FadeGradient
              isHidden={shouldHideLeftGradient && shouldHideRightGradient}
              direction={shouldHideLeftGradient ? 'right' : 'left'}
              itemsCount={items?.length ?? 0}
            />
          </SwiperWrapper>
          {showFullscreen ? (
            <FullScreenVideoPortal
              data={items}
              biteId={biteId}
              activeIndex={activeBlickBiteIndex}
              onClose={handleOnClose}
              adData={ad}
            />
          ) : null}
        </BlickBitesWrapper>
      </InvertedColorsContext.Provider>
    )
  }, [
    accentColor,
    activeBlickBiteIndex,
    ad,
    arrowsVisibility,
    biteId,
    handleOnClose,
    isDesktop,
    items,
    itemsWithOnClick,
    onArrowsVisibility,
    onInit,
    shouldHideLeftGradient,
    shouldHideRightGradient,
    showFullscreen,
    title,
    viewportRef,
  ])

  return [
    useShowVideoCombination(),
    useIsVideoCombinationWrapperCreated(),
  ].every(Boolean)
    ? createPortal(
        widgetContent,
        document.getElementById(
          'blick-bites-combined-wrapper',
        ) as HTMLDivElement,
      )
    : widgetContent
}

export { BlickBitesWrapper }

const widget = {
  kind: ['blick-bites'],
  component: BlickBites,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
